import React from 'react';
import MiniCalendar from '../../components/MiniCalendar';
import { formatDate } from '../../../../../utils/dateUtils';

/**
 * ConflictFilterBar Component
 * Handles searching, type filtering, date range selection, and reason filtering.
 * 
 * @component
 */
const ConflictFilterBar = ({
    isDetectionEnabled,
    activeTab,
    search,
    setSearch,
    resTypeFilter,
    toggleResType,
    setResTypeFilter,
    dateRange,
    setDateRange,
    reasonFilter,
    toggleReason,
    setReasonFilter,
    allReasons,
    activeDropdown,
    setActiveDropdown,
    datePickerRef,
    reasonFilterRef,
    resTypeFilterRef,
    dateMenuStyles,
    reasonMenuStyles,
    handleExpandAll,
    handleCollapseAll,
    formatReason,
    resetFilters,
    hasActiveFilters
}) => {
    
    const getResTypeLabel = () => {
        if (resTypeFilter.length === 0) return 'Keine';
        if (resTypeFilter.length === 3) return 'Alle';
        if (resTypeFilter.length === 1) {
            if (resTypeFilter[0] === 'kolonne') return 'Kolonnen';
            if (resTypeFilter[0] === 'worker') return 'Mitarbeiter';
            if (resTypeFilter[0] === 'vehicle') return 'Fahrzeuge';
        }
        return `${resTypeFilter.length} angewählt`;
    };

    const getDateLabel = () => {
        if (!dateRange.start && !dateRange.end) return 'Dauerhaft';
        if (dateRange.start && dateRange.end) return `${formatDate(dateRange.start)} - ${formatDate(dateRange.end)}`;
        if (dateRange.start) return `Ab ${formatDate(dateRange.start)}`;
        return `Bis ${formatDate(dateRange.end)}`;
    };

    const getReasonLabel = () => {
        if (reasonFilter.length === 0) return 'Keine Gründe';
        if (reasonFilter.length === allReasons.length) return 'Alle Gründe';
        if (reasonFilter.length === 1) return reasonFilter[0];
        return `${reasonFilter.length} Gründe`;
    };

    return (
        <div className="co-filter-bar">
            <div className="co-search-wrapper" style={{ flex: 1.5, marginBottom: 0 }}>
                <span className="co-search-icon">🔍</span>
                <input 
                    type="text" 
                    className="co-search-input"
                    placeholder="Suche..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                />
            </div>

            {/* Resource Type Filter */}
            <div className="co-filter-group" ref={resTypeFilterRef} style={{ position: 'relative' }}>
                <label className="co-filter-label">Ressourcen</label>
                <button 
                    className={`co-select ${activeDropdown === 'resType' ? 'active' : ''}`} 
                    onClick={() => isDetectionEnabled && setActiveDropdown(activeDropdown === 'resType' ? null : 'resType')}
                >
                    <span>{getResTypeLabel()}</span>
                    <span style={{ fontSize: '0.6rem', marginLeft: 8 }}>▼</span>
                </button>
                {activeDropdown === 'resType' && isDetectionEnabled && (
                    <div className="filter-dropdown-menu" style={{ width: 220, padding: 8 }}>
                        <div className="filter-dropdown-menu-list">
                            {[
                                { id: 'kolonne', label: 'Kolonnen', icon: '👥' },
                                { id: 'worker', label: 'Mitarbeiter', icon: '👤' },
                                { id: 'vehicle', label: 'Fahrzeuge', icon: '🚛' }
                            ].map(type => {
                                const isChecked = resTypeFilter.includes(type.id);
                                return (
                                    <div 
                                        key={type.id} 
                                        className="co-conflict-item" 
                                        style={{ cursor: 'pointer', padding: '10px 12px', border: 'none', background: 'transparent' }}
                                        onClick={() => toggleResType(type.id)}
                                    >
                                        <div className={`leader-custom-checkbox ${isChecked ? 'checked' : ''}`} style={{ marginRight: 12 }}>
                                            {isChecked && <span className="checkmark">✓</span>}
                                        </div>
                                        <span style={{ marginRight: 8 }}>{type.icon}</span>
                                        <span style={{ fontSize: 13, fontWeight: 600 }}>{type.label}</span>
                                    </div>
                                );
                            })}
                        </div>
                        <div style={{ 
                            display: 'flex', 
                            justifyContent: 'space-around', 
                            marginTop: 8, 
                            borderTop: '1px solid #f1f5f9', 
                            paddingTop: 8,
                            gap: 8
                        }}>
                            <small 
                                style={{ color: '#6366f1', cursor: 'pointer', fontWeight: 700, padding: '4px 8px' }} 
                                onClick={() => setResTypeFilter(['kolonne', 'worker', 'vehicle'])}
                            >
                                Alle
                            </small>
                            <span style={{ color: '#f1f5f9' }}>|</span>
                            <small 
                                style={{ color: '#ef4444', cursor: 'pointer', fontWeight: 700, padding: '4px 8px' }} 
                                onClick={() => setResTypeFilter([])}
                            >
                                Keine
                            </small>
                        </div>
                    </div>
                )}
            </div>

            {/* Date Filter */}
            <div className="co-filter-group" ref={datePickerRef} style={{ position: 'relative' }}>
                <label className="co-filter-label">Zeitraum</label>
                <button 
                    className={`co-select ${activeDropdown === 'date' ? 'active' : ''}`} 
                    onClick={() => isDetectionEnabled && setActiveDropdown(activeDropdown === 'date' ? null : 'date')}
                >
                    <span>{getDateLabel()}</span>
                    <span style={{ fontSize: '0.6rem', marginLeft: 8 }}>▼</span>
                </button>
                {activeDropdown === 'date' && isDetectionEnabled && (
                    <div className="filter-dropdown-menu" style={{ width: 'auto', minWidth: 540, padding: '12px 16px', ...dateMenuStyles }}>
                        <MiniCalendar dualMonth={true} start={dateRange.start} end={dateRange.end} onSelect={(s, e) => { setDateRange({ start: s, end: e }); if (s && e) setActiveDropdown(null); }} />
                        <div style={{ textAlign: 'center', marginTop: 8, borderTop: '1px solid #f1f5f9', paddingTop: 8 }}>
                            <small style={{ color: '#6366f1', cursor: 'pointer', fontWeight: 700 }} onClick={() => { setDateRange({ start: null, end: null }); setActiveDropdown(null); }}>Zurücksetzen</small>
                        </div>
                    </div>
                )}
            </div>

            {/* Reason Filter */}
            <div className="co-filter-group" ref={reasonFilterRef} style={{ position: 'relative' }}>
                <label className="co-filter-label">Gründe</label>
                <button 
                    className={`co-select ${activeDropdown === 'reason' ? 'active' : ''}`} 
                    onClick={() => isDetectionEnabled && setActiveDropdown(activeDropdown === 'reason' ? null : 'reason')}
                >
                    <span>{getReasonLabel()}</span>
                    <span style={{ fontSize: '0.6rem', marginLeft: 8 }}>▼</span>
                </button>
                {activeDropdown === 'reason' && isDetectionEnabled && (
                    <div className="filter-dropdown-menu" style={{ width: 280, padding: 8, ...reasonMenuStyles }}>
                        <div className="filter-dropdown-menu-list">
                            {allReasons.map(r => {
                                const isChecked = reasonFilter.includes(r);
                                let icon = '📝';
                                if (r.includes('gesperrt')) icon = '🚫';
                                else if (r === 'Mehrfachbelegung') icon = '🔀';
                                else if (r === 'Urlaub') icon = '🏖️';
                                else if (r === 'Krankheit') icon = '🤒';

                                return (
                                    <div 
                                        key={r} 
                                        className="co-conflict-item" 
                                        style={{ cursor: 'pointer', padding: '10px 12px', border: 'none', background: 'transparent' }}
                                        onClick={() => toggleReason(r)}
                                    >
                                        <div className={`leader-custom-checkbox ${isChecked ? 'checked' : ''}`} style={{ marginRight: 12 }}>
                                            {isChecked && <span className="checkmark">✓</span>}
                                        </div>
                                        <span style={{ marginRight: 8 }}>{icon}</span>
                                        <span style={{ fontSize: 13, fontWeight: 600 }}>{r}</span>
                                    </div>
                                );
                            })}
                        </div>
                        <div style={{ 
                            display: 'flex', 
                            justifyContent: 'space-around', 
                            marginTop: 8, 
                            borderTop: '1px solid #f1f5f9', 
                            paddingTop: 8,
                            gap: 8
                        }}>
                            <small 
                                style={{ color: '#6366f1', cursor: 'pointer', fontWeight: 700, padding: '4px 8px' }} 
                                onClick={() => setReasonFilter([...allReasons])}
                            >
                                Alle
                            </small>
                            <span style={{ color: '#f1f5f9' }}>|</span>
                            <small 
                                style={{ color: '#ef4444', cursor: 'pointer', fontWeight: 700, padding: '4px 8px' }} 
                                onClick={() => setReasonFilter([])}
                            >
                                Keine
                            </small>
                        </div>
                    </div>
                )}
            </div>

            {/* Expand/Collapse Actions */}
            {isDetectionEnabled && (activeTab === 'resources' || activeTab === 'archive') && (
                <div style={{ marginLeft: 'auto', display: 'flex', gap: 8, alignSelf: 'flex-end', marginBottom: '4px' }}>
                    {hasActiveFilters && (
                        <button 
                            className="co-reset-filters-btn" 
                            onClick={resetFilters}
                            title="Filter zurücksetzen"
                        >
                            🔄 Reset
                        </button>
                    )}
                    <button className="co-expand-btn" onClick={handleExpandAll}>Alle auf</button>
                    <button className="co-expand-btn" onClick={handleCollapseAll}>Alle zu</button>
                </div>
            )}
        </div>
    );
};

export default ConflictFilterBar;
